import type { MutableRefObject } from 'react';
import {
	createStore,
	createHook,
	createActionsHook,
	type BoundActions,
	type HookActionsFunction,
	type HookFunction,
	type Store as SweetStateStore,
} from 'react-sweet-state';

type CreateSpaceButtonLocationType =
	| 'topCreateNav'
	| 'personalizedTemplatesV2ExperimentSetupTeamSpaceCard'
	| undefined;

type State = {
	showCreateSpaceDrawer: boolean;
	shouldRenderDrawer: boolean;
	createSpaceButtonRef: MutableRefObject<HTMLElement | null>;
	createSpaceButtonLocation: CreateSpaceButtonLocationType;
};

const initialState: State = {
	showCreateSpaceDrawer: false,
	shouldRenderDrawer: false,
	createSpaceButtonRef: {
		current: null,
	} as MutableRefObject<HTMLElement | null>,
	createSpaceButtonLocation: undefined,
};
export const actions = {
	openDrawer:
		(
			value: MutableRefObject<HTMLElement | null>,
			createSpaceButtonLocation: CreateSpaceButtonLocationType,
		) =>
		({ setState }: { setState: any }): void => {
			setState({
				showCreateSpaceDrawer: true,
				shouldRenderDrawer: true,
				createSpaceButtonRef: value,
				createSpaceButtonLocation,
			});
		},
	closeDrawer:
		() =>
		({ setState }: { setState: any }): void => {
			setState({ showCreateSpaceDrawer: false, createSpaceButtonLocation: undefined });
		},
};

export const Store: SweetStateStore<
	State,
	{
		openDrawer: (
			value: MutableRefObject<HTMLElement | null>,
			createSpaceButtonLocation: CreateSpaceButtonLocationType,
		) => ({ setState }: { setState: any }) => void;
		closeDrawer: () => ({ setState }: { setState: any }) => void;
	}
> = createStore({
	initialState,
	actions,
	name: 'createSpaceStore',
});

export const useCreateSpaceStore: HookFunction<
	State,
	BoundActions<
		State,
		{
			openDrawer: (
				value: MutableRefObject<HTMLElement | null>,
				createSpaceButtonLocation: CreateSpaceButtonLocationType,
			) => ({ setState }: { setState: any }) => void;
			closeDrawer: () => ({ setState }: { setState: any }) => void;
		}
	>,
	void
> = createHook(Store);

const getShouldRenderDrawer = (state: { shouldRenderDrawer: boolean }) => {
	return state.shouldRenderDrawer;
};

export const useShouldRenderDrawer: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			openDrawer: (
				value: MutableRefObject<HTMLElement | null>,
				createSpaceButtonLocation: CreateSpaceButtonLocationType,
			) => ({ setState }: { setState: any }) => void;
			closeDrawer: () => ({ setState }: { setState: any }) => void;
		}
	>,
	void
> = createHook(Store, {
	selector: getShouldRenderDrawer,
});

const getIsCreateSpaceDrawerShowing = (state: { showCreateSpaceDrawer: boolean }) => {
	return state.showCreateSpaceDrawer;
};

export const useIsCreateSpaceDrawerShowing: HookFunction<
	boolean,
	BoundActions<
		State,
		{
			openDrawer: (
				value: MutableRefObject<HTMLElement | null>,
				createSpaceButtonLocation: CreateSpaceButtonLocationType,
			) => ({ setState }: { setState: any }) => void;
			closeDrawer: () => ({ setState }: { setState: any }) => void;
		}
	>,
	void
> = createHook(Store, {
	selector: getIsCreateSpaceDrawerShowing,
});
export const useCreateSpaceStoreActions: HookActionsFunction<
	BoundActions<
		State,
		{
			openDrawer: (
				value: MutableRefObject<HTMLElement | null>,
				createSpaceButtonLocation: CreateSpaceButtonLocationType,
			) => ({ setState }: { setState: any }) => void;
			closeDrawer: () => ({ setState }: { setState: any }) => void;
		}
	>
> = createActionsHook(Store);
